<script lang="ts">
import type { CheckboxEmits, CheckboxSlots, CheckboxProps as UCheckboxProps } from '#ui/types';

export interface CheckboxProps<T = boolean> extends Omit<UCheckboxProps<T>, 'ui'> {
	readonly?: boolean;
	ui?: UCheckboxProps<T>['ui'] & {
		outer?: string;
	};
}
</script>

<script setup lang="ts" generic="T = boolean">
import { reactiveOmit } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { useReadonly } from '#layers/ui/composables/use-readonly';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import Checkbox from '#ui/components/Checkbox.vue';

defineOptions({ inheritAttrs: false });

const props = defineProps<CheckboxProps<T>>();
const emit = defineEmits<CheckboxEmits<T>>();
const slots = defineSlots<CheckboxSlots>();

const forwarded = useForwardPropsEmits(reactiveOmit(props, 'readonly', 'ui'), emit);
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly ?? false);

const checkboxUi = computed(() => {
	const { outer: _, ...ui } = props.ui ?? {};

	return mergeUi(ui, { base: props.readonly ? 'opacity-100! cursor-default! pointer-events-none' : '' });
});
</script>

<template>
	<div
		:class="props.ui?.outer"
		@pointerdown.capture="preventPointer"
		@click.capture="preventPointer"
		@keydown.capture="preventKeyboardNavigation"
	>
		<Checkbox
			v-bind="{ ...forwarded, ...$attrs }"
			:disabled="props.disabled"
			:aria-readonly="props.readonly || undefined"
			:ui="checkboxUi"
		>
			<template v-for="(_, slotName) in slots" #[slotName]="slotData: any">
				<slot :name="slotName" v-bind="slotData" />
			</template>
		</Checkbox>
	</div>
</template>
